import { useEffect } from "react"
import { usePage, router } from "@inertiajs/react"
import type { SharedProps } from "@/types"
import { csrfToken } from "@/lib/csrf"

declare global {
  interface Window {
    google?: {
      accounts: {
        id: {
          initialize: (config: Record<string, unknown>) => void
          prompt: () => void
          cancel: () => void
        }
      }
    }
  }
}

export default function GoogleOneTap() {
  const { currentUser } = usePage<SharedProps>().props

  useEffect(() => {
    // Don't show One Tap if already signed in
    if (currentUser) return

    const clientId = document.querySelector<HTMLMetaElement>('meta[name="google-client-id"]')?.content
    if (!clientId) return

    let cancelled = false

    function initOneTap() {
      if (cancelled || !window.google?.accounts?.id) return

      window.google.accounts.id.initialize({
        client_id: clientId,
        callback: handleCredentialResponse,
        auto_select: false,
        cancel_on_tap_outside: true,
      })

      window.google.accounts.id.prompt()
    }

    // Wait for the GSI script to load
    if (window.google?.accounts?.id) {
      initOneTap()
    } else {
      const interval = setInterval(() => {
        if (window.google?.accounts?.id) {
          clearInterval(interval)
          initOneTap()
        }
      }, 200)

      return () => {
        cancelled = true
        clearInterval(interval)
        window.google?.accounts?.id?.cancel()
      }
    }

    return () => {
      cancelled = true
      window.google?.accounts?.id?.cancel()
    }
  }, [currentUser])

  return null
}

function handleCredentialResponse(response: { credential: string }) {
  fetch("/auth/google-one-tap", {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      "X-CSRF-Token": csrfToken(),
    },
    body: JSON.stringify({ credential: response.credential }),
  })
    .then((res) => res.json())
    .then((data) => {
      if (data.redirect_url) {
        router.visit(data.redirect_url)
      }
    })
    .catch(console.error)
}
